import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/auth";

const ALLOWED_THEMES = ["system", "light", "dark"];
const ALLOWED_MOTION = ["full", "reduced"];
const ALLOWED_TEXT_SCALES = ["small", "medium", "large"];
const ALLOWED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "ar", label: "العربية" },
  { code: "zh", label: "中文" },
];

const DOMAINS = [
  { key: "legal", label: "Legal" },
  { key: "housing", label: "Housing" },
  { key: "immigration", label: "Immigration" },
  { key: "retail", label: "Retail" },
  { key: "nourishment", label: "Nourishment" },
  { key: "wellness", label: "Wellness & Wellbeing" },
  { key: "personal_trainers", label: "Personal Trainers" },
  { key: "coaches", label: "Coaches" },
  { key: "therapists", label: "Therapists" },
  { key: "aesthetics", label: "Aesthetics" },
];

// BTC3 Phase 4 · Personalization Center uses the central authentication
// client (`api` from `lib/auth`).  The obsolete localStorage token path
// has been removed — every request now goes through the same axios
// instance whose interceptor attaches the canonical Bearer credential.

export default function PersonalizationCenter() {
  const [prefs, setPrefs] = useState(null);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [p, o] = await Promise.all([
        api.get(`/northstar/personalization`),
        api.get(`/northstar/personalization/domains-order`),
      ]);
      setPrefs(p.data);
      setOrder(o.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Unable to load your personalization preferences."
      );
    }
  };

  useEffect(() => {
    loadAll();
     
  }, []);

  const patchPrefs = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/northstar/personalization`,
        payload,
      );
      setPrefs(data);
      const o = await api.get(`/northstar/personalization/domains-order`);
      setOrder(o.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to save.");
    } finally {
      setSaving(false);
    }
  };

  const toggleConsent = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/northstar/personalization/consent`,
        { personalization_enabled: next },
      );
      setPrefs(data);
      const o = await api.get(`/northstar/personalization/domains-order`);
      setOrder(o.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to update consent.");
    } finally {
      setSaving(false);
    }
  };

  const toggleJourneyMemoryConsent = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/northstar/personalization/journey-memory-consent`,
        { journey_memory_personalization_enabled: next },
      );
      setPrefs(data);
      const o = await api.get(`/northstar/personalization/domains-order`);
      setOrder(o.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to update journey-memory personalization.");
    } finally {
      setSaving(false);
    }
  };

  const toggleInterest = (key) => {
    if (!prefs) return;
    const current = prefs.domain_interests || [];
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    patchPrefs({ domain_interests: next });
  };

  const enabled = prefs?.personalization_enabled === true;
  const jmEnabled = prefs?.journey_memory_personalization_enabled === true;
  const [hints, setHints] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setHints([]);
      return () => {};
    }
    api
      .get(`/northstar/personalization/presentation-hints`)
      .then((r) => {
        if (!cancelled) setHints(Array.isArray(r.data?.hints) ? r.data.hints : []);
      })
      .catch(() => {
        if (!cancelled) setHints([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, jmEnabled]);

  const orderedItems = useMemo(() => order?.items || [], [order]);

  return (
    <div
      data-testid="personalization-center-page"
      className="min-h-screen bg-[#0F1720] text-[#F4EEE0] px-6 sm:px-12 lg:px-24 py-10"
    >
      <header className="max-w-3xl mx-auto mb-8">
        <Link
          to="/northstar"
          data-testid="personalization-back"
          className="text-xs uppercase tracking-widest text-[#A0A8B2] hover:text-[#F4EEE0]"
        >
          ← Northstar
        </Link>
        <h1
          data-testid="personalization-heading"
          className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif"
        >
          Personalization
        </h1>
        <p className="mt-4 text-base text-[#C7CBD1] max-w-2xl">
          Choose how BTC's Northstar experience is tailored for you. Every
          preference below is something you tell us — Northstar never infers
          medical, emotional, or behavioural information about you.
        </p>
      </header>

      {error && (
        <div
          data-testid="personalization-error"
          className="max-w-3xl mx-auto mb-6 p-4 border border-red-500/30 bg-red-500/10 text-red-100 text-sm"
        >
          {error}
        </div>
      )}

      {!prefs ? (
        <p data-testid="personalization-loading" className="max-w-3xl mx-auto">
          Loading…
        </p>
      ) : (
        <main className="max-w-3xl mx-auto space-y-10">
          {/* Consent */}
          <section
            data-testid="personalization-consent-section"
            className="border border-[#2A3540] p-6 rounded-sm"
          >
            <h2 className="text-lg font-medium mb-2">Personalization</h2>
            <p className="text-sm text-[#C7CBD1] mb-4">
              When on, Northstar tailors ordering and display to your
              declared preferences. When off, you see the deterministic
              baseline. Turning personalization off does not delete any
              information; it simply stops applying your preferences.
            </p>
            <label className="flex items-center gap-3">
              <input
                data-testid="personalization-consent-toggle"
                type="checkbox"
                checked={enabled}
                disabled={saving}
                onChange={(e) => toggleConsent(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm">
                {enabled
                  ? "Personalization is on."
                  : "Personalization is off."}
              </span>
            </label>
          </section>

          {/* 9C/9D — Completed BTC journeys as a personalization input */}
          <section
            data-testid="personalization-journey-memory-section"
            className="border border-[#2A3540] p-6 rounded-sm"
          >
            <h2 className="text-lg font-medium mb-2">
              Use completed BTC journeys for personalization
            </h2>
            <p className="text-sm text-[#C7CBD1] mb-4">
              If you turn this on, BTC may use information about completed BTC
              journeys to personalize parts of your Northstar experience. You
              can turn this off at any time. Turning it off stops future
              personalization use but does not delete your journey history.
            </p>
            <p
              data-testid="personalization-journey-memory-distinction"
              className="text-xs text-[#A0A8B2] mb-4"
            >
              This is separate from your Journey History. Turning this off
              stops personalization only; your completed journeys remain
              visible in your history.
            </p>
            <label className="flex items-center gap-3">
              <input
                data-testid="personalization-journey-memory-toggle"
                type="checkbox"
                checked={jmEnabled}
                disabled={saving || !enabled}
                onChange={(e) => toggleJourneyMemoryConsent(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm">
                {!enabled
                  ? "Turn on Personalization above to enable this."
                  : jmEnabled
                  ? "Completed BTC journeys may inform personalization."
                  : "Completed BTC journeys are not used for personalization."}
              </span>
            </label>
          </section>

          {/* Language */}
          <section
            data-testid="personalization-language-section"
            className="border border-[#2A3540] p-6 rounded-sm"
          >
            <h2 className="text-lg font-medium mb-2">Language</h2>
            <p className="text-sm text-[#C7CBD1] mb-4">
              Used to render Northstar strings you have chosen to translate.
            </p>
            <select
              data-testid="personalization-language-select"
              value={prefs.language_preference}
              disabled={saving}
              onChange={(e) =>
                patchPrefs({ language_preference: e.target.value })
              }
              className="bg-[#0F1720] border border-[#2A3540] px-3 py-2 text-sm"
            >
              {ALLOWED_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </section>

          {/* Display */}
          <section
            data-testid="personalization-display-section"
            className="border border-[#2A3540] p-6 rounded-sm"
          >
            <h2 className="text-lg font-medium mb-4">Display & accessibility</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="flex flex-col gap-2 text-sm">
                <span className="text-[#A0A8B2] uppercase text-xs tracking-widest">
                  Theme
                </span>
                <select
                  data-testid="personalization-theme-select"
                  value={prefs.display_preferences?.theme || "system"}
                  disabled={saving}
                  onChange={(e) =>
                    patchPrefs({
                      display_preferences: { theme: e.target.value },
                    })
                  }
                  className="bg-[#0F1720] border border-[#2A3540] px-3 py-2"
                >
                  {ALLOWED_THEMES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-2 text-sm">
                <span className="text-[#A0A8B2] uppercase text-xs tracking-widest">
                  Motion
                </span>
                <select
                  data-testid="personalization-motion-select"
                  value={
                    prefs.display_preferences?.motion_preference || "full"
                  }
                  disabled={saving}
                  onChange={(e) =>
                    patchPrefs({
                      display_preferences: { motion_preference: e.target.value },
                    })
                  }
                  className="bg-[#0F1720] border border-[#2A3540] px-3 py-2"
                >
                  {ALLOWED_MOTION.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-2 text-sm">
                <span className="text-[#A0A8B2] uppercase text-xs tracking-widest">
                  Text scale
                </span>
                <select
                  data-testid="personalization-textscale-select"
                  value={prefs.display_preferences?.text_scale || "medium"}
                  disabled={saving}
                  onChange={(e) =>
                    patchPrefs({
                      display_preferences: { text_scale: e.target.value },
                    })
                  }
                  className="bg-[#0F1720] border border-[#2A3540] px-3 py-2"
                >
                  {ALLOWED_TEXT_SCALES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          {/* 9E — Presentation hints (read-only, deterministic) */}
          <section
            data-testid="personalization-presentation-hints-section"
            className="border border-[#2A3540] p-6 rounded-sm"
          >
            <h2 className="text-lg font-medium mb-2">
              What is showing more prominently for you
            </h2>
            <p className="text-sm text-[#C7CBD1] mb-4">
              When personalization is on, BTC may present a Northstar
              domain more prominently based on your explicit
              personalization settings. These are not recommendations
              — nothing has been chosen for you, and nothing changes
              which domains you can access.
            </p>
            {hints.length === 0 ? (
              <p
                data-testid="personalization-presentation-hints-empty"
                className="text-sm text-[#A0A8B2]"
              >
                Nothing is currently being presented more prominently.
              </p>
            ) : (
              <ul
                data-testid="personalization-presentation-hints-list"
                className="space-y-3"
              >
                {hints.map((h) => (
                  <li
                    key={h.reason_code + ":" + h.domain_key}
                    data-testid={`personalization-hint-${h.domain_key}`}
                    className="border border-[#2A3540] px-4 py-3 text-sm"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="uppercase text-xs tracking-widest text-[#A0A8B2]">
                        {h.domain_key}
                      </span>
                      <span
                        data-testid={`personalization-hint-priority-${h.domain_key}`}
                        className="text-[10px] uppercase tracking-widest text-[#7ED493]"
                      >
                        {h.presentation_priority}
                      </span>
                    </div>
                    <p className="text-[#C7CBD1]">{h.reason_text}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Interests */}
          <section
            data-testid="personalization-interests-section"
            className="border border-[#2A3540] p-6 rounded-sm"
          >
            <h2 className="text-lg font-medium mb-2">
              Northstar interests
            </h2>
            <p className="text-sm text-[#C7CBD1] mb-4">
              Select the Northstar domains you want to see first when
              personalization is on. Domains you do not select remain
              available; they just are not surfaced first.
            </p>
            <div
              data-testid="personalization-interests-grid"
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              {DOMAINS.map((d) => {
                const on = prefs.domain_interests?.includes(d.key);
                return (
                  <label
                    key={d.key}
                    className="flex items-center gap-3 border border-[#2A3540] px-3 py-2 text-sm hover:border-[#3D4A57] cursor-pointer"
                  >
                    <input
                      data-testid={`personalization-interest-${d.key}`}
                      type="checkbox"
                      checked={!!on}
                      disabled={saving}
                      onChange={() => toggleInterest(d.key)}
                      className="h-4 w-4"
                    />
                    <span>{d.label}</span>
                  </label>
                );
              })}
            </div>
          </section>

          {/* Preview / explainability */}
          <section
            data-testid="personalization-preview-section"
            className="border border-[#2A3540] p-6 rounded-sm"
          >
            <h2 className="text-lg font-medium mb-2">
              How your Northstar is ordered
            </h2>
            <p
              data-testid="personalization-baseline-reason"
              className="text-sm text-[#C7CBD1] mb-4"
            >
              {order?.personalization_applied
                ? "Your selected interests are surfaced first. All other Northstar domains remain available."
                : order?.baseline_reason || "Showing the baseline Northstar order."}
            </p>
            <ol data-testid="personalization-order-list" className="space-y-2">
              {orderedItems.map((it, idx) => (
                <li
                  key={it.domain_key}
                  data-testid={`personalization-order-item-${it.domain_key}`}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 border-b border-[#1F2830] pb-2"
                >
                  <span className="text-xs text-[#A0A8B2] tabular-nums">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm font-medium">{it.label}</span>
                  <span
                    data-testid={`personalization-order-reason-${it.domain_key}`}
                    className="text-xs text-[#C7CBD1] sm:ml-auto"
                  >
                    Why am I seeing this? {it.reason}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          <section
            data-testid="personalization-scope-note"
            className="text-xs text-[#A0A8B2]"
          >
            Personalization does not use your medical information, Buddy
            conversations, Ask Northstar conversations, or any inferred
            attributes about you.
          </section>
        </main>
      )}
    </div>
  );
}
